import { useEffect, useMemo, useRef, useState } from "react";

import {
  emitDevHarness,
  forceFlushDevHarness,
  getDevHarnessSnapshot,
  setDevHarnessContext,
  startPostSmokeWatchdog,
  subscribeDevHarnessSnapshot,
} from "@/dev-harness/telemetry";
import type { DevHarnessSnapshot } from "@/dev-harness/types";
import type { RuntimeBus } from "@/runtime/bus";
import type {
  EngineProfile,
  InstallRecord,
  Lane,
  MessageRecord,
  Phase,
  StorageDiagnostics,
  ThreadRecord,
} from "@/runtime/events";
import { startMicrophoneCapture } from "@/audio/capture";
import { type AppRoute } from "@/app/router";
import {
  CHECKING_NETWORK,
  EMPTY_STORAGE,
  PHASE_LABELS,
  createLaneInstallState,
  formatBytes,
  type LaneInstallState,
  type NetworkBannerState,
} from "@/app/ui-state";
import { APP_CONFIG } from "@/config/app-config";
import { HYBRID_PROFILE, LOCAL_PROFILE } from "@/config/engine-profiles";
import { LOCAL_LANE_MANIFESTS } from "@/config/model-manifest";
import {
  getLaneSmokeProbeLabel,
  mergeSmokeRecord,
  pendingSmokeRecord,
} from "@/runtime/lane-smoke";
import { ConversationSession, type ConversationCoordinatorTelemetry } from "@/runtime/conversation-session";
import { runLaneSmokeCheckInWorker } from "@/runtime/heavy-operation-client";
import { orchestrateLaneInstall } from "@/install/install-orchestrator";
import { createTtsAdapter } from "@/adapters/factory";
import { shouldBargeIn } from "@/runtime/interrupt";
import { nextPhaseFromEvent } from "@/runtime/state-machine";
import { openAppDatabase } from "@/storage/db";
import { listInstallRecords, saveInstallRecord } from "@/storage/manifests";
import { getStorageDiagnostics, requestPersistentStorage } from "@/storage/opfs";
import { loadSettings, saveSettings } from "@/storage/settings";
import {
  appendMessage,
  createThread,
  listMessages,
  listThreads,
  renameThread,
  updateThreadProfile,
} from "@/storage/threads";

interface AppProps {
  bus: RuntimeBus;
}

function relayConversationCoordinatorTelemetry(event: ConversationCoordinatorTelemetry): void {
  emitDevHarness({
    kind: event.event === "conversation.result.discarded" ? "incident" : "transition",
    level: event.event === "conversation.result.discarded" ? "warning" : "info",
    event: event.event,
    data: {
      turn_id: event.turnId,
      epoch: event.epoch,
      lane: event.lane ?? null,
      reason: event.reason ?? null,
    },
  });
}

interface CloudDrafts {
  llm: string;
  tts: string;
}

interface OfflineGateCheck {
  key: string;
  label: string;
  passed: boolean;
  detail: string;
}

const LOCAL_LANES = ["vad", "stt", "llm", "tts"] as Lane[];
const TOTAL_LOCAL_MODEL_BYTES = LOCAL_LANES.reduce(
  (total, lane) => total + LOCAL_LANE_MANIFESTS[lane].bytes,
  0,
);

function buildSafeBanner(mode: "checking" | "online" | "offline"): NetworkBannerState {
  if (mode === "online") {
    return {
      mode,
      value: "OFF",
      caption: "Server reachable. Treat the device as network-connected and not safe for sensitive speech.",
    };
  }

  if (mode === "offline") {
    return {
      mode,
      value: "LIKELY ON",
      caption: "Server unreachable. Inputs stay local to this foundation build on the device.",
    };
  }

  return CHECKING_NETWORK;
}

function profileName(profile: EngineProfile): string {
  return profile.llm.startsWith("cloud") || profile.tts.startsWith("cloud")
    ? "Hybrid"
    : "Fully Local";
}

function detectHomeScreenLaunch(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  if (window.matchMedia?.("(display-mode: standalone)").matches) {
    return true;
  }

  return Boolean((navigator as Navigator & { standalone?: boolean }).standalone);
}

function describePersistentStorageState(homeScreenLaunch: boolean): string {
  return homeScreenLaunch
    ? "Home Screen mode is the iPhone target. Treat persisted storage plus passing lane checks as the offline-ready bar."
    : "Browser-tab mode on iPhone cannot reliably complete large local-lane installs — iOS WebKit evicts the tab process under memory pressure and we have ruled out a page-side fix. Add the app to the Home Screen, relaunch it there, then install the local lanes. Tab-mode installs are best-effort and may be interrupted; the segmented installer will resume where it left off if you come back.";
}

function buildOfflineGateChecks(input: {
  checkedLaneCount: number;
  homeScreenLaunch: boolean;
  serviceWorkerReady: boolean;
  storageDiagnostics: StorageDiagnostics;
}): OfflineGateCheck[] {
  const { checkedLaneCount, homeScreenLaunch, serviceWorkerReady, storageDiagnostics } = input;
  const modelFootprintCovered =
    storageDiagnostics.opfsAvailable && storageDiagnostics.quota >= TOTAL_LOCAL_MODEL_BYTES;
  const persistentStorageReady =
    storageDiagnostics.opfsAvailable &&
    storageDiagnostics.persistApiAvailable &&
    storageDiagnostics.persisted;

  return [
    {
      key: "home-screen",
      label: "Home Screen Launch",
      passed: homeScreenLaunch,
      detail: homeScreenLaunch
        ? "Standalone display mode detected."
        : "Launch from the iPhone Home Screen icon before calling the device offline-ready.",
    },
    {
      key: "shell-cache",
      label: "Shell Cache",
      passed: serviceWorkerReady,
      detail: serviceWorkerReady
        ? "Service worker registration completed."
        : "The shell cache is not ready yet.",
    },
    {
      key: "model-footprint",
      label: "Local Model Footprint",
      passed: modelFootprintCovered,
      detail: modelFootprintCovered
        ? `Reported quota covers the ${formatBytes(TOTAL_LOCAL_MODEL_BYTES)} local model plan.`
        : `Reported quota does not yet cover the ${formatBytes(TOTAL_LOCAL_MODEL_BYTES)} local model plan.`,
    },
    {
      key: "persistent-storage",
      label: "Persistent Storage",
      passed: persistentStorageReady,
      detail: persistentStorageReady
        ? "Persistent storage has been verified for this origin."
        : homeScreenLaunch
          ? "Persistent storage has not been verified yet from the Home Screen install."
          : "Persistent storage should be verified from the Home Screen install, not a browser tab.",
    },
    {
      key: "lane-checks",
      label: "Lane Checks",
      passed: checkedLaneCount === LOCAL_LANES.length,
      detail:
        checkedLaneCount === LOCAL_LANES.length
          ? "All four local lanes passed their on-device checks."
          : `${checkedLaneCount}/${LOCAL_LANES.length} local lanes have passed their on-device checks.`,
    },
  ];
}

function getCurrentInstallRecord(records: InstallRecord[], lane: Lane): InstallRecord | undefined {
  const manifest = LOCAL_LANE_MANIFESTS[lane];

  return [...records]
    .reverse()
    .find(
      (record) =>
        record.lane === lane &&
        record.id === manifest.id &&
        record.version === manifest.version,
    );
}

function getCurrentInstallRecords(records: InstallRecord[]): InstallRecord[] {
  return (["vad", "stt", "llm", "tts"] as Lane[])
    .map((lane) => getCurrentInstallRecord(records, lane))
    .filter((record): record is InstallRecord => Boolean(record));
}

function laneStatusFromRecord(record: InstallRecord): LaneInstallState["status"] {
  if (record.status !== "ready") {
    return "error";
  }

  if (record.smoke?.status === "passed") {
    return "ready";
  }

  if (record.smoke?.status === "failed") {
    return "error";
  }

  return "installed";
}

function applyLaneRecord(
  state: Record<Lane, LaneInstallState>,
  lane: Lane,
  record: InstallRecord,
): Record<Lane, LaneInstallState> {
  return {
    ...state,
    [lane]: {
      ...state[lane],
      installed: record.status === "ready",
      status: laneStatusFromRecord(record),
      record,
      received: state[lane].manifest.bytes,
      total: state[lane].manifest.bytes,
      errorMessage: record.errorMessage,
    },
  };
}

export function App({ bus }: AppProps) {
  // Start with the default LOCAL_PROFILE's TTS adapter. If the active thread
  // later selects a HYBRID_PROFILE (cloud TTS), the effect below swaps the
  // adapter on the live session so the same session instance keeps its
  // abort/interrupt plumbing.
  const session = useMemo(
    () =>
      new ConversationSession({
        bus,
        ttsAdapter: createTtsAdapter(LOCAL_PROFILE, { bus }),
        onTelemetry: relayConversationCoordinatorTelemetry,
      }),
    [bus],
  );

  const [phase, setPhase] = useState<Phase>("booting");
  const [route, setRoute] = useState<AppRoute>("conversation");
  const [networkBanner, setNetworkBanner] = useState<NetworkBannerState>(CHECKING_NETWORK);
  const [statusNote, setStatusNote] = useState("Bootstrapping repository foundation.");
  const [storageDiagnostics, setStorageDiagnostics] = useState(EMPTY_STORAGE);
  const [laneStates, setLaneStates] = useState<Record<Lane, LaneInstallState>>(
    createLaneInstallState(LOCAL_LANE_MANIFESTS),
  );
  const [threads, setThreads] = useState<ThreadRecord[]>([]);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [messages, setMessages] = useState<MessageRecord[]>([]);
  const [assistantDraft, setAssistantDraft] = useState("");
  const [partialTranscript, setPartialTranscript] = useState("");
  const [composerText, setComposerText] = useState("");
  const [updateReady, setUpdateReady] = useState(false);
  const [serviceWorkerReady, setServiceWorkerReady] = useState(false);
  const [micPermission, setMicPermission] = useState("unknown");
  const [micLabel, setMicLabel] = useState("Microphone not requested yet");
  const [recordingMicTurn, setRecordingMicTurn] = useState(false);
  const [installingLane, setInstallingLane] = useState<Lane | null>(null);
  const [checkingLane, setCheckingLane] = useState<Lane | null>(null);
  const [cloudDrafts, setCloudDrafts] = useState<CloudDrafts>({ llm: "", tts: "" });
  const [devHarnessSnapshot, setDevHarnessSnapshot] = useState<DevHarnessSnapshot>(
    () => getDevHarnessSnapshot(),
  );
  const [homeScreenLaunch, setHomeScreenLaunch] = useState(() => detectHomeScreenLaunch());

  const activeThreadRef = useRef<ThreadRecord | null>(null);
  const messagesRef = useRef<MessageRecord[]>([]);
  const assistantDraftRef = useRef("");
  const captureStopRef = useRef<(() => Promise<{
    durationMs: number;
    mono: Float32Array;
    sampleRate: number;
  }>) | null>(null);
  const networkModeRef = useRef<NetworkBannerState["mode"] | null>(null);

  useEffect(() => {
    assistantDraftRef.current = assistantDraft;
  }, [assistantDraft]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    activeThreadRef.current =
      threads.find((thread) => thread.id === activeThreadId) ?? null;
  }, [activeThreadId, threads]);

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      emitDevHarness({
        kind: "summary",
        event: "app.bootstrap.start",
        msg: "App bootstrap started",
      });

      try {
        await openAppDatabase();
        const settings = await loadSettings();
        const diagnostics = await getStorageDiagnostics();
        const installRecords = await listInstallRecords();
        const storedThreads = await listThreads();
        const params = new URLSearchParams(window.location.search);
        const requestedRoute = params.get("route") === "settings" ? "settings" : settings.preferredRoute;

        let nextThreads = storedThreads;
        let nextActiveThreadId = storedThreads[0]?.id ?? null;

        if (params.get("intent") === "new-chat" || !storedThreads.length) {
          const created = await createThread(LOCAL_PROFILE);
          nextThreads = await listThreads();
          nextActiveThreadId = created.id;
        }

        const nextMessages = nextActiveThreadId ? await listMessages(nextActiveThreadId) : [];

        if (cancelled) {
          return;
        }

        setRoute(requestedRoute);
        setStorageDiagnostics(diagnostics);
        setThreads(nextThreads);
        setActiveThreadId(nextActiveThreadId);
        setMessages(nextMessages);
        const currentInstallRecords = getCurrentInstallRecords(installRecords);
        const checkedInstallRecords = currentInstallRecords.filter(
          (record) => record.smoke?.status === "passed",
        );
        setLaneStates(() => {
          const next = createLaneInstallState(LOCAL_LANE_MANIFESTS);
          currentInstallRecords.forEach((record) => {
            next[record.lane] = applyLaneRecord(next, record.lane, record)[record.lane];
          });
          return next;
        });
        setPhase(checkedInstallRecords.length === 4 ? "idle" : "provisioning");
        setStatusNote(
          checkedInstallRecords.length === 4
            ? "Foundation boot complete. All lane bundles passed their on-device probes."
            : `Foundation boot complete. ${checkedInstallRecords.length} of 4 lanes have passed their on-device probes.`,
        );
        bus.publish({ type: "runtime.ready" });
        emitDevHarness({
          kind: "summary",
          event: "app.bootstrap.ok",
          data: {
            route: requestedRoute,
            thread_count: nextThreads.length,
            active_thread_id: nextActiveThreadId,
            install_record_count: currentInstallRecords.length,
            checked_lane_count: checkedInstallRecords.length,
            opfs_available: diagnostics.opfsAvailable,
            persist_api_available: diagnostics.persistApiAvailable,
            persisted: diagnostics.persisted,
            persistent_permission_state: diagnostics.permissionState,
          },
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : "Bootstrap failed";
        setPhase("error");
        setStatusNote(message);
        emitDevHarness({
          level: "error",
          kind: "incident",
          event: "app.bootstrap.fail",
          msg: message,
        });
      }
    }

    void bootstrap();

    return () => {
      cancelled = true;
    };
  }, [bus]);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) {
      return;
    }

    let cancelled = false;

    async function registerWorker() {
      emitDevHarness({
        kind: "summary",
        event: "service_worker.register.start",
      });

      try {
        const registration = await navigator.serviceWorker.register("/sw.js");

        if (cancelled) {
          return;
        }

        setServiceWorkerReady(true);
        emitDevHarness({
          kind: "summary",
          event: "service_worker.register.ok",
          data: {
            scope: registration.scope,
            has_waiting_worker: Boolean(registration.waiting),
          },
        });

        if (registration.waiting) {
          setUpdateReady(true);
        }

        registration.addEventListener("updatefound", () => {
          const installing = registration.installing;

          if (!installing) {
            return;
          }

          installing.addEventListener("statechange", () => {
            if (installing.state === "installed" && navigator.serviceWorker.controller) {
              setUpdateReady(true);
              emitDevHarness({
                kind: "transition",
                event: "service_worker.update_ready",
              });
            }
          });
        });

        navigator.serviceWorker.addEventListener("message", (event) => {
          if (event.data?.type === "sw.activated") {
            setStatusNote(`Shell cache ready: ${event.data.version}`);
            emitDevHarness({
              kind: "transition",
              event: "service_worker.activated",
              data: { version: event.data.version },
            });
          }
        });
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Service worker registration failed";
        setStatusNote(message);
        emitDevHarness({
          level: "error",
          kind: "incident",
          event: "service_worker.register.fail",
          msg: message,
        });
      }
    }

    void registerWorker();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setDevHarnessContext({
      threadId: activeThreadId,
      route,
      phase,
    });
  }, [activeThreadId, phase, route]);

  useEffect(() => {
    return subscribeDevHarnessSnapshot(setDevHarnessSnapshot);
  }, []);

  useEffect(() => {
    if (networkModeRef.current === networkBanner.mode) {
      return;
    }

    networkModeRef.current = networkBanner.mode;
    emitDevHarness({
      level: networkBanner.mode === "offline" ? "warning" : "info",
      kind: "transition",
      event: "network.banner.mode",
      data: {
        mode: networkBanner.mode,
        value: networkBanner.value,
      },
      msg: networkBanner.caption,
    });
  }, [networkBanner.caption, networkBanner.mode, networkBanner.value]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const syncLaunchMode = () => {
      setHomeScreenLaunch(detectHomeScreenLaunch());
    };

    syncLaunchMode();
    window.addEventListener("pageshow", syncLaunchMode);
    window.addEventListener("focus", syncLaunchMode);

    return () => {
      window.removeEventListener("pageshow", syncLaunchMode);
      window.removeEventListener("focus", syncLaunchMode);
    };
  }, []);

  useEffect(() => {
    const unsubscribe = bus.subscribe((event) => {
      setPhase((current) => nextPhaseFromEvent(current, event));

      switch (event.type) {
        case "install.progress":
          setLaneStates((current) => ({
            ...current,
            [event.lane]: {
              ...current[event.lane],
              status: "installing",
              received: event.received,
              total: event.total,
            },
          }));
          break;
        case "install.ready":
          setStatusNote(`${event.manifestId} verified and staged in OPFS.`);
          break;
        case "smoke.start":
          setStatusNote(`Running ${event.lane.toUpperCase()} on-device check with ${event.probe}.`);
          setLaneStates((current) => ({
            ...current,
            [event.lane]: {
              ...current[event.lane],
              status: "checking",
              errorMessage: undefined,
            },
          }));
          break;
        case "smoke.ok":
          setStatusNote(event.summary);
          break;
        case "smoke.fail":
          setStatusNote(event.message);
          break;
        case "stt.partial":
          setPartialTranscript(event.text);
          break;
        case "stt.final":
          void handleRecognizedTranscript(event.text);
          break;
        case "llm.delta":
          assistantDraftRef.current = event.text;
          setAssistantDraft(event.text);
          break;
        case "llm.done":
          void commitAssistantDraft();
          break;
        case "tts.audio":
          setStatusNote("Playing local TTS output.");
          break;
        case "vad.barge_in":
          setStatusNote("Barge-in triggered. Playback interrupted and a new turn can begin.");
          break;
        case "tts.done":
          setPartialTranscript("");
          break;
        case "error":
          setStatusNote(`${event.domain}: ${event.message}`);
          break;
        default:
          break;
      }
    });

    return unsubscribe;
  }, [bus]);

  useEffect(() => {
    return () => {
      if (captureStopRef.current) {
        void captureStopRef.current().catch(() => undefined);
        captureStopRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    let intervalId = 0;
    let cancelled = false;

    async function probeNetwork() {
      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), 2500);

      try {
        const response = await fetch(`/healthz?ts=${Date.now()}`, {
          cache: "no-store",
          signal: controller.signal,
        });
        if (!cancelled) {
          setNetworkBanner(buildSafeBanner(response.ok ? "online" : "offline"));
        }
      } catch {
        if (!cancelled) {
          setNetworkBanner(buildSafeBanner("offline"));
        }
      } finally {
        window.clearTimeout(timeoutId);
      }
    }

    setNetworkBanner(buildSafeBanner("checking"));
    void probeNetwork();
    intervalId = window.setInterval(() => void probeNetwork(), 10000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, []);

  async function refreshThreads(threadId: string | null = activeThreadId): Promise<void> {
    const storedThreads = await listThreads();
    setThreads(storedThreads);

    if (threadId) {
      setMessages(await listMessages(threadId));
    }
  }

  async function handleRecognizedTranscript(text: string): Promise<void> {
    const thread = activeThreadRef.current;
    if (!thread) {
      emitDevHarness({
        level: "warning",
        kind: "incident",
        event: "speech.transcript.drop",
        msg: "Transcript received without an active thread",
      });
      return;
    }

    emitDevHarness({
      kind: "summary",
      event: "speech.transcript.final",
      data: {
        thread_id: thread.id,
        text_length: text.length,
        preview: text.slice(0, 120),
      },
    });

    const userMessage = await appendMessage(thread, "user", text);
    setPartialTranscript("");
    await refreshThreads(thread.id);
    assistantDraftRef.current = "";
    setAssistantDraft("");
    await session.runAssistantTurn([...messagesRef.current, userMessage]);
  }

  async function commitAssistantDraft(): Promise<void> {
    const thread = activeThreadRef.current;
    const draft = assistantDraftRef.current;

    if (!thread || !draft) {
      return;
    }

    await appendMessage(thread, "assistant", draft);
    setAssistantDraft("");
    await refreshThreads(thread.id);
    emitDevHarness({
      kind: "summary",
      event: "assistant.reply.commit",
      data: {
        thread_id: thread.id,
        text_length: draft.length,
        preview: draft.slice(0, 120),
      },
    });
  }

  async function handleSendText(): Promise<void> {
    const thread = activeThreadRef.current;
    const text = composerText.trim();

    if (!thread || !text) {
      emitDevHarness({
        level: "warning",
        kind: "incident",
        event: "composer.send.rejected",
        data: {
          has_thread: Boolean(thread),
          text_length: text.length,
        },
      });
      return;
    }

    emitDevHarness({
      kind: "summary",
      event: "composer.send.start",
      data: {
        thread_id: thread.id,
        text_length: text.length,
        preview: text.slice(0, 120),
      },
    });

    const userMessage = await appendMessage(thread, "user", text);
    setComposerText("");
    await refreshThreads(thread.id);
    assistantDraftRef.current = "";
    setAssistantDraft("");
    await session.runAssistantTurn([...messagesRef.current, userMessage]);
  }

  async function handleMicTurn(): Promise<void> {
    emitDevHarness({
      kind: "summary",
      event: "speech.turn.start",
      data: {
        phase,
      },
    });

    if (shouldBargeIn(phase)) {
      session.interrupt();
      await new Promise((resolve) => window.setTimeout(resolve, 120));
      emitDevHarness({
        kind: "transition",
        event: "speech.turn.barge_in",
      });
    }

    if (captureStopRef.current) {
      const stopCapture = captureStopRef.current;
      captureStopRef.current = null;
      setRecordingMicTurn(false);
      setPhase("transcribing");
      setStatusNote("Stopping microphone capture and running local VAD + Whisper.");

      try {
        const captured = await stopCapture();
        emitDevHarness({
          kind: "summary",
          event: "microphone.capture.stop",
          data: {
            duration_ms: captured.durationMs,
            sample_rate: captured.sampleRate,
            samples: captured.mono.length,
          },
        });
        await session.runMicTurn(captured);
      } catch (error) {
        const message = error instanceof Error ? error.message : "Microphone capture stop failed";
        setStatusNote(message);
        emitDevHarness({
          level: "error",
          kind: "incident",
          event: "microphone.capture.stop.fail",
          msg: message,
        });
      }

      return;
    }

    try {
      const capture = await startMicrophoneCapture();
      captureStopRef.current = capture.stop;
      setMicPermission(capture.permission);
      setMicLabel(capture.label);
      setRecordingMicTurn(true);
      setPhase("listening");
      setStatusNote("Recording a local mic turn. Tap again to stop and transcribe.");
      emitDevHarness({
        kind: "summary",
        event: "microphone.permission.result",
        data: {
          permission: capture.permission,
          label: capture.label,
        },
      });
      emitDevHarness({
        kind: "summary",
        event: "microphone.capture.start",
        data: {
          label: capture.label,
        },
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Microphone request failed";
      setMicPermission("error");
      setMicLabel(message);
      setStatusNote(message);
      emitDevHarness({
        level: "error",
        kind: "incident",
        event: "microphone.permission.result",
        data: {
          permission: "error",
        },
        msg: message,
      });
    }
  }

  async function runLaneCheckWithRecord(
    lane: Lane,
    baseRecord: InstallRecord,
  ): Promise<InstallRecord> {
    const manifest = LOCAL_LANE_MANIFESTS[lane];
    setCheckingLane(lane);
    setPhase("provisioning");
    setLaneStates((current) => ({
      ...current,
      [lane]: {
        ...current[lane],
        installed: true,
        status: "checking",
        record: baseRecord,
        received: manifest.bytes,
        total: manifest.bytes,
        errorMessage: undefined,
      },
    }));
    emitDevHarness({
      kind: "summary",
      event: "smoke.lane.start",
      data: {
        lane,
        manifest_id: manifest.id,
        probe: getLaneSmokeProbeLabel(lane),
      },
    });

    try {
      const smoke = await runLaneSmokeCheckInWorker(manifest, {
        onRuntimeEvent: (event) => bus.publish(event),
      });
      const checkedRecord = mergeSmokeRecord(baseRecord, smoke);
      await saveInstallRecord(checkedRecord);
      const checkedRecords = getCurrentInstallRecords(await listInstallRecords()).filter(
        (record) => record.smoke?.status === "passed",
      );
      setLaneStates((current) => applyLaneRecord(current, lane, checkedRecord));
      setPhase(checkedRecords.length === 4 ? "idle" : "provisioning");
      setStatusNote(smoke.summary);
      emitDevHarness({
        kind: "summary",
        event: "smoke.lane.ok",
        data: {
          lane,
          manifest_id: manifest.id,
          probe: getLaneSmokeProbeLabel(lane),
          checked_lanes: checkedRecords.length,
        },
      });
      // Investigation hook: every confirmed iPhone install crash to date has
      // happened in the 1.6–2.8 s window between smoke.lane.ok and an
      // unexplained page reload, with no error/pagehide/freeze fired. Start a
      // sub-second watchdog series that posts memory + lifecycle snapshots
      // and force-flushes after each tick — gives us trajectory inside the
      // death window even if iOS abrupt-kills the tab.
      startPostSmokeWatchdog(`smoke.lane.ok:${lane}`);
      void forceFlushDevHarness("smoke.lane.ok");
      return checkedRecord;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Lane check failed";
      const failedRecord = mergeSmokeRecord(baseRecord, {
        status: "failed",
        checkedAt: new Date().toISOString(),
        probe: getLaneSmokeProbeLabel(lane),
        summary: message,
      });
      await saveInstallRecord(failedRecord);
      const checkedRecords = getCurrentInstallRecords(await listInstallRecords()).filter(
        (record) => record.smoke?.status === "passed",
      );
      setLaneStates((current) => applyLaneRecord(current, lane, failedRecord));
      setPhase(checkedRecords.length === 4 ? "idle" : "provisioning");
      setStatusNote(message);
      emitDevHarness({
        level: "error",
        kind: "incident",
        event: "smoke.lane.fail",
        data: {
          lane,
          manifest_id: manifest.id,
          probe: getLaneSmokeProbeLabel(lane),
        },
        msg: message,
      });
      return failedRecord;
    } finally {
      setCheckingLane(null);
    }
  }

  async function handleRunLaneCheck(lane: Lane): Promise<void> {
    if (installingLane || (checkingLane && checkingLane !== lane)) {
      return;
    }

    const existing = getCurrentInstallRecord(await listInstallRecords(), lane);

    if (!existing || existing.status !== "ready") {
      setStatusNote(`Install ${LOCAL_LANE_MANIFESTS[lane].label} before running its check.`);
      return;
    }

    await runLaneCheckWithRecord(lane, existing);
  }

  async function handleInstallLane(lane: Lane): Promise<void> {
    if (checkingLane || (installingLane && installingLane !== lane)) {
      return;
    }

    const manifest = LOCAL_LANE_MANIFESTS[lane];
    setInstallingLane(lane);
    setPhase("provisioning");
    setStatusNote(`Installing ${manifest.label} into OPFS.`);
    setLaneStates((current) => ({
      ...current,
      [lane]: {
        ...current[lane],
        installed: false,
        status: "installing",
        received: 0,
        total: manifest.bytes,
        errorMessage: undefined,
      },
    }));
    emitDevHarness({
      kind: "summary",
      event: "install.lane.start",
      data: {
        lane,
        manifest_id: manifest.id,
        bytes: manifest.bytes,
        files: manifest.files.length,
      },
    });

    try {
      const record: InstallRecord = await orchestrateLaneInstall(manifest, { bus });
      const pendingRecord = mergeSmokeRecord(record, pendingSmokeRecord(lane));
      await saveInstallRecord(pendingRecord);
      const installRecords = getCurrentInstallRecords(await listInstallRecords());
      setLaneStates((current) => applyLaneRecord(current, lane, pendingRecord));
      setPhase("provisioning");
      setStatusNote(
        `${manifest.label} downloaded into OPFS. Running the ${getLaneSmokeProbeLabel(lane)} device check next.`,
      );
      setStorageDiagnostics(await getStorageDiagnostics());
      emitDevHarness({
        kind: "summary",
        event: "install.lane.ok",
        data: {
          lane,
          manifest_id: manifest.id,
          installed_lanes: installRecords.length,
        },
      });
      setInstallingLane(null);
      await runLaneCheckWithRecord(lane, pendingRecord);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Install failed";
      setStatusNote(message);
      setLaneStates((current) => ({
        ...current,
        [lane]: {
          ...current[lane],
          installed: false,
          status: "error",
          errorMessage: message,
        },
      }));
      emitDevHarness({
        level: "error",
        kind: "incident",
        event: "install.lane.fail",
        data: {
          lane,
          manifest_id: manifest.id,
        },
        msg: message,
      });
    } finally {
      setInstallingLane(null);
    }
  }

  async function handleRequestPersistentStorage(): Promise<void> {
    emitDevHarness({
      kind: "summary",
      event: "storage.persist.request",
    });
    const result = await requestPersistentStorage();
    const diagnostics = await getStorageDiagnostics();
    setStorageDiagnostics(diagnostics);

    if (!result.supported) {
      setStatusNote("Persistent storage API is unavailable in this browser context.");
    } else if (result.granted) {
      setStatusNote(
        homeScreenLaunch
          ? "Persistent storage is enabled for this Home Screen install."
          : "Persistent storage is enabled for this origin. Relaunch from the Home Screen before calling the device offline-ready.",
      );
    } else if (result.errorMessage) {
      setStatusNote(`Persistent storage request failed: ${result.errorMessage}`);
    } else {
      setStatusNote(
        `Persistent storage was not enabled. ${describePersistentStorageState(homeScreenLaunch)}`,
      );
    }

    emitDevHarness({
      level: result.granted ? "info" : "warning",
      kind: result.granted ? "summary" : "incident",
      event: "storage.persist.result",
      data: {
        granted: result.granted,
        supported: result.supported,
        requested: result.requested,
        before_persisted: result.beforePersisted,
        after_persisted: result.afterPersisted,
        permission_state_before: result.permissionStateBefore,
        permission_state_after: result.permissionStateAfter,
        error_message: result.errorMessage ?? null,
      },
    });
  }

  async function handleNewThread(profile: EngineProfile = LOCAL_PROFILE): Promise<void> {
    const thread = await createThread(profile);
    setActiveThreadId(thread.id);
    setMessages([]);
    await refreshThreads(thread.id);
    emitDevHarness({
      kind: "summary",
      event: "thread.create",
      data: {
        thread_id: thread.id,
        profile,
      },
    });
  }

  async function handleRenameThread(thread: ThreadRecord): Promise<void> {
    const nextTitle = window.prompt("Rename thread", thread.title)?.trim();

    if (!nextTitle) {
      return;
    }

    await renameThread(thread, nextTitle);
    await refreshThreads(thread.id);
    emitDevHarness({
      kind: "summary",
      event: "thread.rename",
      data: {
        thread_id: thread.id,
        title: nextTitle,
      },
    });
  }

  async function applyProfileToActiveThread(profile: EngineProfile): Promise<void> {
    if (!activeThreadRef.current) {
      return;
    }

    const updated = await updateThreadProfile(activeThreadRef.current, profile);
    setThreads((current) => current.map((thread) => (thread.id === updated.id ? updated : thread)));
    setStatusNote(`Thread profile set to ${profileName(updated.profile)}.`);
    emitDevHarness({
      kind: "summary",
      event: "thread.profile.update",
      data: {
        thread_id: updated.id,
        profile: updated.profile,
      },
    });
  }

  async function handleRouteChange(nextRoute: AppRoute): Promise<void> {
    setRoute(nextRoute);
    const settings = await loadSettings();
    await saveSettings({
      ...settings,
      preferredRoute: nextRoute,
      lastUpdatedAt: new Date().toISOString(),
    });
    emitDevHarness({
      kind: "transition",
      event: "route.change",
      data: {
        route: nextRoute,
      },
    });
  }

  async function handleSelectThread(threadId: string): Promise<void> {
    setActiveThreadId(threadId);
    setMessages(await listMessages(threadId));
    emitDevHarness({
      kind: "transition",
      event: "thread.select",
      data: {
        thread_id: threadId,
      },
    });
  }

  const activeThread = activeThreadRef.current;
  const checkedLaneCount = LOCAL_LANES.filter((lane) => laneStates[lane].status === "ready").length;
  const offlineGateChecks = buildOfflineGateChecks({
    checkedLaneCount,
    homeScreenLaunch,
    serviceWorkerReady,
    storageDiagnostics,
  });
  const passedOfflineGateCount = offlineGateChecks.filter((check) => check.passed).length;
  const offlineReady = offlineGateChecks.every((check) => check.passed);
  const blockedOfflineGateLabels = offlineGateChecks
    .filter((check) => !check.passed)
    .map((check) => check.label.toLowerCase());
  const offlineGateSummary = offlineReady
    ? "Home Screen launch, shell cache, storage, and lane checks meet the current iPhone Airplane Mode Alpha gate."
    : `Still blocked by ${blockedOfflineGateLabels.join(", ")}.`;

  return (
    <div className="page-shell">
      <header className={`network-banner mode-${networkBanner.mode}`}>
        <p className="banner-label">Airplane Mode Proxy</p>
        <p className="banner-value">{networkBanner.value}</p>
        <p className="banner-copy">{networkBanner.caption}</p>
      </header>

      <main className="app-frame">
        <section className="control-rail">
          <div className="panel hero-panel">
            <p className="eyebrow">Speech-to-Speech Runtime Kit</p>
            <h1>Private Local Chat</h1>
            <p className="hero-copy">
              Local-first speech chatbot for Android Chrome and iOS WebKit.
              The release path is the Home Screen PWA, with offline readiness
              gated on standalone launch, durable local storage, and passing
              local VAD, STT, LLM, and TTS checks.
            </p>
          </div>

          <div className="panel summary-grid">
            <article className="summary-card">
              <span className="summary-label">Phase</span>
              <strong>{PHASE_LABELS[phase]}</strong>
              <small>{statusNote}</small>
            </article>
            <article className="summary-card">
              <span className="summary-label">Offline Ready</span>
              <strong>{offlineReady ? "Yes" : "Not Yet"}</strong>
              <small>
                Gate checks: {passedOfflineGateCount}/{offlineGateChecks.length} · Home Screen:{" "}
                {homeScreenLaunch ? "yes" : "no"} · Persistent:{" "}
                {storageDiagnostics.persisted ? "yes" : "no"} · Checked lanes:{" "}
                {checkedLaneCount}/{LOCAL_LANES.length}
              </small>
            </article>
            <article className="summary-card">
              <span className="summary-label">Storage</span>
              <strong>{formatBytes(storageDiagnostics.usage)}</strong>
              <small>
                of {formatBytes(storageDiagnostics.quota)} · persistent:{" "}
                {storageDiagnostics.persisted ? "yes" : "no"} · target footprint:{" "}
                {formatBytes(TOTAL_LOCAL_MODEL_BYTES)}
              </small>
            </article>
          </div>

          <div className="panel">
            <div className="panel-head">
              <div>
                <h2>Lane Installs</h2>
                <p className="support-copy">
                  Each lane now installs its own real model bundle into OPFS,
                  then runs a real on-device probe. Audio lanes use{" "}
                  {APP_CONFIG.fixtureAudioLabel}; the LLM lane runs a deterministic
                  prompt/response check before the live conversation path uses it.
                </p>
              </div>
            </div>
            <div className="lane-list">
              {(Object.keys(laneStates) as Lane[]).map((lane) => {
                const laneState = laneStates[lane];
                const busy =
                  installingLane === lane ||
                  checkingLane === lane ||
                  Boolean(
                    (installingLane && installingLane !== lane) ||
                      (checkingLane && checkingLane !== lane),
                  );
                return (
                  <article key={lane} className={`lane-card status-${laneState.status}`}>
                    <div className="lane-copy">
                      <p className="lane-title">{laneState.manifest.label}</p>
                      <p className="lane-meta">
                        {laneState.manifest.id} · {formatBytes(laneState.manifest.bytes)} ·{" "}
                        {laneState.manifest.files.length} files
                      </p>
                      {laneState.record?.smoke ? (
                        <p className="lane-check">
                          Check:{" "}
                          <strong>
                            {laneState.record.smoke.status === "passed"
                              ? "passed"
                              : laneState.record.smoke.status === "failed"
                                ? "failed"
                                : "pending"}
                          </strong>{" "}
                          · {laneState.record.smoke.summary}
                        </p>
                      ) : null}
                      {laneState.errorMessage ? (
                        <p className="lane-error">{laneState.errorMessage}</p>
                      ) : null}
                    </div>
                    <div className="lane-progress">
                      <strong>
                        {laneState.status === "ready"
                          ? "Checked"
                          : laneState.status === "checking"
                            ? "Running Check"
                            : laneState.status === "installed"
                              ? "Downloaded"
                          : laneState.status === "installing"
                            ? `${formatBytes(laneState.received)} / ${formatBytes(
                                laneState.total,
                              )}`
                            : laneState.status === "error"
                              ? "Error"
                              : "Missing"}
                      </strong>
                      <small>
                        OPFS files: {laneState.installed ? "present" : "missing"}
                      </small>
                      <div className="lane-actions">
                        <button
                          type="button"
                          className={laneState.installed ? "button-secondary" : "button-primary"}
                          onClick={() => void handleInstallLane(lane)}
                          disabled={busy}
                        >
                          {installingLane === lane
                            ? "Installing..."
                            : laneState.installed
                              ? "Reinstall"
                              : "Install Model"}
                        </button>
                        <button
                          type="button"
                          className="button-secondary"
                          onClick={() => void handleRunLaneCheck(lane)}
                          disabled={!laneState.installed || busy}
                        >
                          {checkingLane === lane ? "Checking..." : "Run Check"}
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>

          <div className="panel">
            <div className="panel-head">
              <h2>Device Gates</h2>
            </div>
            <div className="action-row">
              <button
                type="button"
                className="button-secondary"
                onClick={() => void handleRequestPersistentStorage()}
              >
                Check / Request Persistent Storage
              </button>
              <button
                type="button"
                className="button-secondary"
                onClick={() => void handleMicTurn()}
              >
                {recordingMicTurn ? "Finish Local Mic Turn" : "Start Local Mic Turn"}
              </button>
            </div>
            <dl className="diagnostic-grid">
              <div>
                <dt>Launch Mode</dt>
                <dd>{homeScreenLaunch ? "home screen" : "browser tab"}</dd>
              </div>
              <div>
                <dt>Gate Checks</dt>
                <dd>
                  {passedOfflineGateCount}/{offlineGateChecks.length}
                </dd>
              </div>
              <div>
                <dt>Persistent</dt>
                <dd>{storageDiagnostics.persisted ? "yes" : "no"}</dd>
              </div>
              <div>
                <dt>Local Footprint</dt>
                <dd>{formatBytes(TOTAL_LOCAL_MODEL_BYTES)}</dd>
              </div>
            </dl>
            <p className="support-copy">
              Mic permission: <strong>{micPermission}</strong> · {micLabel} · Fixture:{" "}
              <strong>{APP_CONFIG.fixtureAudioLabel}</strong>
            </p>
            <p className="support-copy">{offlineGateSummary}</p>
            {offlineGateChecks.map((check) => (
              <p key={check.key} className="support-copy">
                {check.label}: <strong>{check.passed ? "ready" : "blocked"}</strong> ·{" "}
                {check.detail}
              </p>
            ))}
            <p className="support-copy">
              {describePersistentStorageState(homeScreenLaunch)}
            </p>
          </div>
        </section>

        <section className="conversation-shell">
          <div className="panel conversation-panel">
            <div className="panel-head">
              <div>
                <p className="eyebrow">Conversation</p>
                <h2>{activeThread?.title ?? "No Thread"}</h2>
              </div>
              <div className="action-row compact">
                <button
                  type="button"
                  className="button-secondary"
                  onClick={() => void handleNewThread()}
                >
                  New Thread
                </button>
                <button
                  type="button"
                  className="button-secondary"
                  onClick={() => void handleRouteChange(route === "settings" ? "conversation" : "settings")}
                >
                  {route === "settings" ? "Close Settings" : "Settings"}
                </button>
              </div>
            </div>

            <div className="thread-strip">
              {threads.map((thread) => (
                <button
                  key={thread.id}
                  type="button"
                  className={`thread-pill ${thread.id === activeThreadId ? "is-active" : ""}`}
                  onClick={() => void handleSelectThread(thread.id)}
                >
                  <span>{thread.title}</span>
                  <small>{profileName(thread.profile)}</small>
                </button>
              ))}
            </div>

            <div className="transcript-banner">
              <div>
                <span className="summary-label">Live Transcript</span>
                <strong>{partialTranscript || "Waiting for VAD / STT events"}</strong>
              </div>
              {activeThread ? (
                <button
                  type="button"
                  className="link-button"
                  onClick={() => void handleRenameThread(activeThread)}
                >
                  Rename
                </button>
              ) : null}
            </div>

            <div className="message-list">
              {messages.map((message) => (
                <article key={message.id} className={`message-bubble role-${message.role}`}>
                  <span className="message-role">{message.role}</span>
                  <p>{message.text}</p>
                </article>
              ))}

              {assistantDraft ? (
                <article className="message-bubble role-assistant draft">
                  <span className="message-role">assistant draft</span>
                  <p>{assistantDraft}</p>
                </article>
              ) : null}
            </div>

            <div className="composer">
              <textarea
                value={composerText}
                onChange={(event) => setComposerText(event.target.value)}
                placeholder="Type a message to exercise the real local LLM + OpenMOSS path without using the microphone."
              />
              <div className="action-row">
                <button
                  type="button"
                  className="button-primary"
                  onClick={() => void handleSendText()}
                  disabled={!composerText.trim()}
                >
                  Send Text Turn
                </button>
                <button
                  type="button"
                  className="button-secondary"
                  onClick={() => void handleMicTurn()}
                >
                  {recordingMicTurn ? "Finish Mic Turn" : "Start Mic Turn"}
                </button>
              </div>
            </div>
          </div>

          {route === "settings" ? (
            <aside className="panel settings-panel">
              <div className="panel-head">
                <h2>Settings & Diagnostics</h2>
              </div>

              <div className="settings-block">
                <h3>Thread Profile</h3>
                <p className="support-copy">
                  VAD and STT stay local. LLM and TTS can move to cloud adapters
                  later without changing the UI contract.
                </p>
                <div className="action-row">
                  <button
                    type="button"
                    className="button-secondary"
                    onClick={() => void applyProfileToActiveThread(LOCAL_PROFILE)}
                  >
                    Use Fully Local Profile
                  </button>
                  <button
                    type="button"
                    className="button-secondary"
                    onClick={() => void applyProfileToActiveThread(HYBRID_PROFILE)}
                  >
                    Use Hybrid Profile
                  </button>
                </div>
                <dl className="diagnostic-grid">
                  <div>
                    <dt>VAD</dt>
                    <dd>{activeThread?.profile.vad ?? LOCAL_PROFILE.vad}</dd>
                  </div>
                  <div>
                    <dt>STT</dt>
                    <dd>{activeThread?.profile.stt ?? LOCAL_PROFILE.stt}</dd>
                  </div>
                  <div>
                    <dt>LLM</dt>
                    <dd>{activeThread?.profile.llm ?? LOCAL_PROFILE.llm}</dd>
                  </div>
                  <div>
                    <dt>TTS</dt>
                    <dd>{activeThread?.profile.tts ?? LOCAL_PROFILE.tts}</dd>
                  </div>
                </dl>
              </div>

              <div className="settings-block">
                <h3>Cloud Keys</h3>
                <p className="support-copy">
                  These draft values stay in memory only in this foundation build.
                </p>
                <label className="stacked-field">
                  <span>LLM Provider Key</span>
                  <input
                    type="password"
                    value={cloudDrafts.llm}
                    onChange={(event) =>
                      setCloudDrafts((current) => ({ ...current, llm: event.target.value }))
                    }
                    placeholder="Not persisted"
                  />
                </label>
                <label className="stacked-field">
                  <span>TTS Provider Key</span>
                  <input
                    type="password"
                    value={cloudDrafts.tts}
                    onChange={(event) =>
                      setCloudDrafts((current) => ({ ...current, tts: event.target.value }))
                    }
                    placeholder="Not persisted"
                  />
                </label>
              </div>

              <div className="settings-block">
                <h3>Diagnostics</h3>
                <dl className="diagnostic-grid">
                  <div>
                    <dt>Launch Mode</dt>
                    <dd>{homeScreenLaunch ? "standalone" : "browser tab"}</dd>
                  </div>
                  <div>
                    <dt>Quota</dt>
                    <dd>{formatBytes(storageDiagnostics.quota)}</dd>
                  </div>
                  <div>
                    <dt>Usage</dt>
                    <dd>{formatBytes(storageDiagnostics.usage)}</dd>
                  </div>
                  <div>
                    <dt>Available</dt>
                    <dd>{formatBytes(storageDiagnostics.available)}</dd>
                  </div>
                  <div>
                    <dt>Required Local Bytes</dt>
                    <dd>{formatBytes(TOTAL_LOCAL_MODEL_BYTES)}</dd>
                  </div>
                  <div>
                    <dt>Persistent</dt>
                    <dd>{storageDiagnostics.persisted ? "yes" : "no"}</dd>
                  </div>
                  <div>
                    <dt>Offline Gate</dt>
                    <dd>
                      {passedOfflineGateCount}/{offlineGateChecks.length} ready
                    </dd>
                  </div>
                  <div>
                    <dt>Persist API</dt>
                    <dd>{storageDiagnostics.persistApiAvailable ? "yes" : "no"}</dd>
                  </div>
                  <div>
                    <dt>Persisted API</dt>
                    <dd>{storageDiagnostics.persistedApiAvailable ? "yes" : "no"}</dd>
                  </div>
                  <div>
                    <dt>Permission</dt>
                    <dd>{storageDiagnostics.permissionState}</dd>
                  </div>
                  <div>
                    <dt>OPFS</dt>
                    <dd>{storageDiagnostics.opfsAvailable ? "yes" : "no"}</dd>
                  </div>
                  <div>
                    <dt>Shell Cache</dt>
                    <dd>{serviceWorkerReady ? "ready" : "missing"}</dd>
                  </div>
                  <div>
                    <dt>Update Prompt</dt>
                    <dd>{updateReady ? "new shell ready" : "none"}</dd>
                  </div>
                </dl>
                {updateReady ? (
                  <button
                    type="button"
                    className="button-primary"
                    onClick={() => {
                      navigator.serviceWorker.controller?.postMessage({
                        type: "sw.skip-waiting",
                      });
                      window.location.reload();
                    }}
                  >
                    Reload Updated Shell
                  </button>
                ) : null}
              </div>

              {APP_CONFIG.devHarnessEnabled ? (
                <div className="settings-block">
                  <h3>Development Harness</h3>
                  <p className="support-copy">
                    Client activity is queued locally and mirrored back to the server
                    when connectivity exists.
                  </p>
                  <dl className="diagnostic-grid">
                    <div>
                      <dt>Queue</dt>
                      <dd>{devHarnessSnapshot.queueCount}</dd>
                    </div>
                    <div>
                      <dt>Receiver</dt>
                      <dd>{devHarnessSnapshot.lastError ? "error" : "ready"}</dd>
                    </div>
                    <div>
                      <dt>Last Flush</dt>
                      <dd>
                        {devHarnessSnapshot.lastFlushAt
                          ? new Date(devHarnessSnapshot.lastFlushAt).toLocaleTimeString()
                          : "pending"}
                      </dd>
                    </div>
                    <div>
                      <dt>Page Id</dt>
                      <dd>{devHarnessSnapshot.pageId ?? "pending"}</dd>
                    </div>
                  </dl>
                  {devHarnessSnapshot.lastError ? (
                    <p className="support-copy">{devHarnessSnapshot.lastError}</p>
                  ) : null}
                </div>
              ) : null}
            </aside>
          ) : null}
        </section>
      </main>

      <footer className="app-footer">
        <span>Version {APP_CONFIG.version}</span>
        <span>{new Date(APP_CONFIG.buildDate).toLocaleString()}</span>
        <a href={APP_CONFIG.sourceArchivePath}>Source</a>
        <a href="/LICENSE.txt">License</a>
        <a href="/THIRD_PARTY_NOTICES.md">Notices</a>
      </footer>
    </div>
  );
}
